import { mutatePurchaseOrders } from "@/api/purchaseOrder/usePurchaseOrder";
import { users } from "@/api/users/useUsersQuery";
import { ItemAutoComplete } from "@/custom/features/ui/autoComplete/item/item-autoComplete";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/Alert";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { PartnerAutoComplete } from "@/features/ui/autoComplete/partner/partner-autoComplete";
import { PurchaseOrderCategoryAutoComplete } from "@/features/ui/autoComplete/purchaseOrderCategory/purchaseOrderCategory-autoComplete";

import { UserAutoComplete } from "@/features/ui/autoComplete/user/user-autoComplete";
import { handleErrorResponse } from "@/utils/errorMessage";
import { translateWithPartnerType } from "@/utils/partnerType";
import { validateFormValues } from "@/utils/validation";
import { Button, Flex, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  PartnersGet200ResponseRowsInnerTypeEnum,
  ScmApiPurchaseOrdersWithPurchaseOrderItemsPostRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconCalendar, IconPlus } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MantineReactTable, MRT_ColumnDef } from "mantine-react-table";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type FormType = {
  itemCode: string | undefined;
  quantity: string | undefined;
  sourceLocationCode: string | undefined;
  orderDate: Date | undefined;
  deliveryDeadline: Date | undefined;
};

export const PurchaseOrderCreateItemsForm = () => {
  const { closeModal } = useModal();
  const { data: userInfo } = useQuery(users.who({}));
  const partnerType = userInfo?.data?.partner?.type;
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate: postMutate } = useMutation(
    (params: ScmApiPurchaseOrdersWithPurchaseOrderItemsPostRequest) =>
      mutatePurchaseOrders
        .createItems(params)
        .mutationFn(params as ScmApiPurchaseOrdersWithPurchaseOrderItemsPostRequest | any),
    {
      onSuccess: (data) => {
        closeModal("");
        queryClient.invalidateQueries(["purchaseOrders"]);
        customAlert(
          t("message.createSuccess", {
            item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
          }),
          t("common.createSuccess"),
          "green",
        );
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({
            error,
            errorContext: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
          }),
          t("common.createFail", {
            item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
          }),
          "red",
        );
      },
    },
  );

  const form = useForm({
    initialValues: {
      partnerCode: undefined,
      code: undefined,
      name: undefined,
      orderDate: new Date(),
      deliveryDeadline: new Date(),
      userCode: undefined,
      sourceLocationCode: undefined,
      purchaseOrderCategoryName: undefined,
    },
  });

  const [purchaseOrderItems, setPurchaseOrderItems] = useState<FormType[]>([
    {
      itemCode: undefined,
      quantity: undefined,
      sourceLocationCode: undefined,
      orderDate: undefined,
      deliveryDeadline: undefined,
    },
  ]);

  const addRow = () => {
    setPurchaseOrderItems((prev) => [
      ...prev,
      {
        itemCode: undefined,
        quantity: undefined,
        sourceLocationCode: form.values.sourceLocationCode || undefined,
        orderDate: form.values.orderDate || undefined,
        deliveryDeadline: form.values.deliveryDeadline || undefined,
      },
    ]);
  };

  const handleChange = (rowIndex: number, key: keyof FormType, value: any) => {
    const newData = [...purchaseOrderItems];
    newData[rowIndex][key] = value;
    setPurchaseOrderItems(newData);
  };

  const handleSubmit = () => {
    postMutate({
      purchaseOrdersWithPurchaseOrderItemsPostRequest: {
        code: form.values.code ?? "",
        name: form.values.name,
        partnerCode: form.values.partnerCode ?? "",
        direction: "INBOUND",
        orderDate: form.values.orderDate.toISOString().split("T")[0],
        deliveryDeadline: form.values.deliveryDeadline.toISOString().split("T")[0],
        userCode: form.values.userCode,
        purchaseOrderCategoryName: form.values.purchaseOrderCategoryName,
        sourceLocationCode: form.values.sourceLocationCode,
        purchaseOrderItems: purchaseOrderItems
          .filter((item) => !!item.itemCode)
          .map((item) => ({
            itemCode: item.itemCode,
            quantity: item.quantity,
            sourceLocationCode: item.sourceLocationCode,
            deliveryDeadline: item.deliveryDeadline?.toISOString().split("T")[0],
            orderDate: item.orderDate?.toISOString().split("T")[0],
          })),
      },
    });
  };

  const columns: MRT_ColumnDef<FormType>[] = [
    {
      accessorKey: "itemCode",
      header: t("item.code"),
      enableSorting: false,
      size: 360,
      Cell: ({ row }) => (
        <ItemAutoComplete
          // getItemType={["PRODUCT", "SEMI_PRODUCT"]}
          placeholder={t("placeholder.select", { item: t("item.code") })}
          value={row.original.itemCode}
          onChange={(value) => handleChange(row.index, "itemCode", value)}
        />
      ),
    },
    {
      accessorKey: "quantity",
      header: t("inventory.quantity"),
      enableSorting: false,
      Cell: ({ row }) => (
        <TextInput
          placeholder={t("placeholder.input", { item: t("inventory.quantity") })}
          value={row.original.quantity}
          onChange={(e) => handleChange(row.index, "quantity", e.currentTarget.value)}
        />
      ),
    },
    {
      accessorKey: "sourceLocationCode",
      header: translateWithPartnerType(t, "purchaseOrder.toLocationName", partnerType),
      enableSorting: false,
      Cell: ({ row }) => (
        <LocationAutoComplete
          placeholder={t("placeholder.select", {
            item: translateWithPartnerType(t, "purchaseOrder.toLocationName", partnerType),
          })}
          value={
            row.original.sourceLocationCode !== undefined
              ? row.original.sourceLocationCode
              : form.values.sourceLocationCode
          }
          onChange={(value) => handleChange(row.index, "sourceLocationCode", value)}
        />
      ),
    },
    {
      accessorKey: "orderDate",
      header: translateWithPartnerType(t, "purchaseOrder.orderDate", partnerType),
      enableSorting: false,
      Cell: ({ row }) => (
        <DateInput
          popoverProps={{ withinPortal: true }}
          icon={<IconCalendar size="1.2rem" />}
          valueFormat="YYYY-MM-DD"
          defaultValue={new Date()} // 오늘 날짜를 기본값으로 설정
          value={
            row.original.orderDate !== undefined ? row.original.orderDate : form.values.orderDate
          }
          locale="ko"
          firstDayOfWeek={0}
          onChange={(value) => handleChange(row.index, "orderDate", value)}
        />
      ),
    },
    {
      accessorKey: "deliveryDeadline",
      header: t("order.deliveryDate"),
      enableSorting: false,
      Cell: ({ row }) => (
        <DateInput
          popoverProps={{ withinPortal: true }}
          icon={<IconCalendar size="1.2rem" />}
          valueFormat="YYYY-MM-DD"
          value={
            row.original.deliveryDeadline !== undefined
              ? row.original.deliveryDeadline
              : form.values.deliveryDeadline
          }
          locale="ko"
          firstDayOfWeek={0}
          minDate={form.values.orderDate ? new Date(form.values.orderDate) : undefined}
          onChange={(value) => handleChange(row.index, "deliveryDeadline", value)}
        />
      ),
    },
  ];

  return (
    <Flex direction={"column"} gap="md" style={{ flex: 1 }} w="80rem">
      <Flex direction={"row"} gap="md">
        <Flex direction={"column"} gap="md" style={{ flex: 1 }}>
          <PartnerAutoComplete
            label={t("partner.partner")}
            placeholder={t("placeholder.select", { item: t("partner.partner") })}
            {...form.getInputProps("partnerCode")}
            partnerType={[
              partnerType ?? PartnersGet200ResponseRowsInnerTypeEnum.SALES,
              PartnersGet200ResponseRowsInnerTypeEnum.BI_DIRECTION,
            ]}
            withAsterisk
          />
          <TextInput
            label={translateWithPartnerType(t, "purchaseOrder.code", partnerType)}
            placeholder={t("placeholder.input", {
              item: translateWithPartnerType(t, "purchaseOrder.code", partnerType),
            })}
            {...form.getInputProps("code")}
          />
          <TextInput
            label={translateWithPartnerType(t, "purchaseOrder.name", partnerType)}
            placeholder={t("placeholder.input", {
              item: translateWithPartnerType(t, "purchaseOrder.name", partnerType),
            })}
            {...form.getInputProps("name")}
          />
          <UserAutoComplete
            label={t("order.manager")}
            placeholder={t("placeholder.select", { item: t("order.manager") })}
            {...form.getInputProps("userCode")}
          />
        </Flex>
        <Flex direction={"column"} gap="md" style={{ flex: 1 }}>
          <LocationAutoComplete
            label={translateWithPartnerType(t, "purchaseOrder.toLocationName", partnerType)}
            placeholder={t("placeholder.select", {
              item: translateWithPartnerType(t, "purchaseOrder.toLocationName", partnerType),
            })}
            {...form.getInputProps("sourceLocationCode")}
          />
          <PurchaseOrderCategoryAutoComplete
            label={translateWithPartnerType(t, "purchaseOrder.categoryName", partnerType)}
            placeholder={t("placeholder.select", {
              item: translateWithPartnerType(t, "purchaseOrder.categoryName", partnerType),
            })}
            {...form.getInputProps("purchaseOrderCategoryName")}
          />
          <DateInput
            popoverProps={{ withinPortal: true }}
            icon={<IconCalendar size="1.2rem" />}
            label={translateWithPartnerType(t, "purchaseOrder.orderDate", partnerType)}
            valueFormat="YYYY-MM-DD"
            locale="ko"
            firstDayOfWeek={0}
            {...form.getInputProps("orderDate")}
          />
          <DateInput
            popoverProps={{ withinPortal: true }}
            icon={<IconCalendar size="1.2rem" />}
            label={t("order.deliveryDate")}
            minDate={form.values.orderDate ? new Date(form.values.orderDate) : undefined}
            valueFormat="YYYY-MM-DD"
            locale="ko"
            firstDayOfWeek={0}
            {...form.getInputProps("deliveryDeadline")}
          />
        </Flex>
      </Flex>
      <Flex direction={"column"} gap="md">
        <MantineReactTable
          columns={columns}
          data={purchaseOrderItems}
          enableColumnFilters={false}
          enableGlobalFilter={false}
          enableColumnActions={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableHiding={false}
          renderTopToolbarCustomActions={() => (
            <Button leftIcon={<IconPlus />} onClick={addRow}>
              {t("common.newRowAdditional")}
            </Button>
          )}
          mantineTopToolbarProps={{
            style: { justifyContent: "flex-end" }, // 왼쪽-오른쪽 정렬
          }}
        />
      </Flex>
      <Flex justify="flex-end" gap={"md"} style={{ marginTop: "16px" }}>
        <Button color="gray" onClick={closeModal}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={() => handleSubmit()}
          disabled={validateFormValues({
            pageType: "purchaseOrder",
            values: form.values,
          })}
        >
          {t("common.create", {
            item: translateWithPartnerType(t, "purchaseOrder.both", partnerType),
          })}
        </Button>
      </Flex>
    </Flex>
  );
};
